/**
 * Monte Carlo trade shuffle: the same test the server's command line runs.
 * The closed trades are replayed many times in random order; the result is the
 * same every time, but the drawdown on the way is not. It answers "was the
 * drawdown I saw typical, or did the order of trades just happen to be kind?"
 */
export type MonteCarlo = {
  iterations: number;
  trades: number;
  /** Max drawdown (%) at the 5th, 25th, 50th, 75th and 95th percentile of the shuffles. */
  p5: number;
  p25: number;
  p50: number;
  p75: number;
  p95: number;
  /** The drawdown the backtest actually had (%). */
  actual: number;
  /** Share of shuffles (%) with a smaller drawdown than the actual one. */
  actualPercentile: number;
  /** Share of shuffles (%) where equity fell below 10% of the starting capital. */
  riskOfRuin: number;
  verdict: "robust" | "lucky" | "very-lucky" | "risky";
};

export type MonteCarloTrade = {
  pnl: number;
  /** Account balance after this trade. */
  balance?: number;
  qty?: number;
  entry_price?: number;
  is_open?: boolean;
};

/**
 * Does the strategy size its trades from the account (2% risk, 50% of equity…)?
 * Then a trade's result scales with the account at that moment. True when
 * position value follows the balance across the run.
 */
function sizesByEquity(equityBefore: number[], notional: number[]): boolean {
  const n = equityBefore.length;
  if (n < 10) return false;
  let min = Infinity;
  let max = 0;
  for (const e of equityBefore) {
    if (e < min) min = e;
    if (e > max) max = e;
  }
  // The balance barely moved: both readings give the same answer.
  if (!(min > 0) || max / min < 1.3) return false;
  const x = equityBefore.map(Math.log);
  const y = notional.map((v) => Math.log(Math.max(v, 1e-12)));
  const mean = (a: number[]) => a.reduce((sum, v) => sum + v, 0) / a.length;
  const mx = mean(x);
  const my = mean(y);
  let sxy = 0;
  let sxx = 0;
  let syy = 0;
  for (let i = 0; i < n; i++) {
    sxy += (x[i]! - mx) * (y[i]! - my);
    sxx += (x[i]! - mx) ** 2;
    syy += (y[i]! - my) ** 2;
  }
  return sxx > 0 && syy > 0 && sxy / Math.sqrt(sxx * syy) >= 0.5;
}

/** Small seeded generator, so the same backtest always shows the same numbers. */
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Keeps the work bounded on runs with tens of thousands of trades. */
const MAX_STEPS = 6_000_000;

/**
 * @param percentSized true when the engine sizes orders as a percent of equity.
 *   Strategies that size themselves from the account are detected from their trades.
 */
export function runMonteCarlo(
  trades: MonteCarloTrade[],
  initialCapital: number,
  percentSized: boolean,
  actualMaxDrawdownPct: number,
  iterations = 1000,
  seed = 42
): MonteCarlo | null {
  if (!(initialCapital > 0)) return null;
  const pnls: number[] = [];
  const before: number[] = [];
  const notional: number[] = [];
  let running = initialCapital;
  for (const t of trades) {
    if (t.is_open || !Number.isFinite(t.pnl)) continue;
    // The account just before this trade closed.
    const equity = t.balance != null && Number.isFinite(t.balance) && t.balance - t.pnl > 0 ? t.balance - t.pnl : running;
    pnls.push(t.pnl);
    before.push(equity);
    notional.push(Math.abs((t.qty ?? 0) * (t.entry_price ?? 0)));
    running = equity + t.pnl;
  }
  if (pnls.length < 2) return null;

  // Sized from the account: compound. Fixed size: shuffle the money results.
  const compounding = percentSized || sizesByEquity(before, notional);
  // What gets shuffled is how each trade went (its result on the position). How
  // much of the account was in the market at each step stays as it was: a
  // strategy that puts in less as it grows keeps doing so in every shuffle.
  const sized = compounding && notional.every((v, i) => v > 0 && before[i]! > 0);
  const exposure = sized ? notional.map((v, i) => v / before[i]!) : null;
  const returns = !compounding
    ? pnls
    : sized
      ? pnls.map((pnl, i) => pnl / notional[i]!)
      : pnls.map((pnl, i) => (before[i]! > 0 ? pnl / before[i]! : 0));

  const runs = Math.max(200, Math.min(iterations, Math.floor(MAX_STEPS / returns.length)));
  const rand = mulberry32(seed);
  const maxDDs = new Float64Array(runs);
  const shuffled = Float64Array.from(returns);
  const ruinAt = initialCapital * 0.1;
  let ruins = 0;

  for (let i = 0; i < runs; i++) {
    // Fisher-Yates, in place: each run starts from the previous order.
    for (let j = shuffled.length - 1; j > 0; j--) {
      const k = Math.floor(rand() * (j + 1));
      const tmp = shuffled[j]!;
      shuffled[j] = shuffled[k]!;
      shuffled[k] = tmp;
    }
    let equity = initialCapital;
    let peak = initialCapital;
    let maxDD = 0;
    let ruined = false;
    for (let j = 0; j < shuffled.length; j++) {
      equity = !compounding
        ? equity + shuffled[j]!
        : equity * (1 + (exposure ? exposure[j]! * shuffled[j]! : shuffled[j]!));
      if (equity <= ruinAt) {
        ruined = true;
        break;
      }
      if (equity > peak) peak = equity;
      const dd = ((peak - equity) / peak) * 100;
      if (dd > maxDD) maxDD = dd;
    }
    if (ruined) ruins++;
    maxDDs[i] = ruined ? 100 : maxDD;
  }

  maxDDs.sort();
  const at = (p: number) => maxDDs[Math.floor(((runs - 1) * p) / 100)]!;
  const actual = Math.abs(actualMaxDrawdownPct);
  let below = 0;
  while (below < runs && maxDDs[below]! < actual) below++;
  const actualPercentile = (below / runs) * 100;
  const riskOfRuin = (ruins / runs) * 100;

  return {
    iterations: runs,
    trades: returns.length,
    p5: at(5),
    p25: at(25),
    p50: at(50),
    p75: at(75),
    p95: at(95),
    actual,
    actualPercentile,
    riskOfRuin,
    // Same thresholds as the server's report, but "lucky" needs a real gap: when
    // every order gives nearly the same drawdown, being a hair under them is not luck.
    verdict:
      riskOfRuin > 20
        ? "risky"
        : at(50) - actual <= Math.max(1, at(50) * 0.05)
          ? "robust"
          : actualPercentile < 10
            ? "very-lucky"
            : actualPercentile < 25
              ? "lucky"
              : "robust",
  };
}
